import { ActionIcon, Badge, Box, Card, Space, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { useDiContainer } from '@root/Services/DI';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { RuleEditCard } from './Design';
import { ReplacementOptions } from '@apis/TagManager/model/replacementOptions';
import { RuleEditor } from './Model';
import { useIdGen } from '@root/Services/IdGen';
import { injectable } from 'tsyringe';
import { useDisclosure } from '@mantine/hooks';
import styled from '@emotion/styled';
import { Plus, Trash } from 'tabler-icons-react';
import { AnchorButton } from '@root/Design/Primitives';
import { TagKeyCombobox, TagValueCombobox } from '@root/Components/Resources/Common/ResourceTags';

@injectable()
class ReplacementEditor {
    public replacementOptions: ReplacementOptions[] = [];
    public constructor() {}
    public init(ruleEditor: RuleEditor) {
        const rule = ruleEditor.rule!;
        rule.Parameters ??= {};
        rule.Parameters.Syntax ??= {};
        rule.Parameters.Syntax.ReplacementOptions ??= [{}];
        this.replacementOptions = rule.Parameters.Syntax.ReplacementOptions;
        return this;
    }

    public removeItem = (item: ReplacementOptions) => {
        const idx = this.replacementOptions.indexOf(item);
        if (idx >= 0) {
            this.replacementOptions.splice(idx, 1);
        }
    };
    public addItem = () => {
        this.replacementOptions.push({});
    };
    public getErrors() {
        const result: string[] = [];
        const blanks = this.replacementOptions.filter((o) =>
            [o.KeyToCheck, o.KeyToReplace, o.ValueToCheck, o.ValueToReplace].some((r) => r === undefined || r === '')
        );
        if (blanks.length) {
            result.push('Some tag replacement rules are not filled in');
        }
        if (!this.replacementOptions.length) {
            result.push('No tag replacement rules have been set');
        }
        return result;
    }
}

export const ReplaceTagsCard = observer(({ ruleEditor }: { ruleEditor: RuleEditor }) => {
    const di = useDiContainer();
    const typeEditor = useMemo(() => di.resolve(ReplacementEditor).init(ruleEditor), []);
    const { getId } = useIdGen();
    const onChange = useCallback(() => {
        ruleEditor.setTypeErrors('Replacement', typeEditor.getErrors());
    }, [ruleEditor, typeEditor]);
    const addItem = useCallback(() => {
        typeEditor.addItem();
        onChange();
    }, [typeEditor.addItem]);
    useEffect(onChange, []);
    const [showingErrors, { open: showErrors }] = useDisclosure(false);
    return (
        <RuleEditCard title="Replace Tags" description="Add rules for finding and replacing tags" accent onBlur={showErrors}>
            {typeEditor.replacementOptions.map((o) => (
                <ReplacementRuleItem item={o} showingErrors={showingErrors} onRemove={typeEditor.removeItem} onChange={onChange} key={getId(o)} />
            ))}
            <AnchorButton size="sm" color="primary" icon={<Plus />} text="Add Another" onClick={addItem} data-atid="AddRuleButton" />
        </RuleEditCard>
    );
});

const ReplacementRuleItem = observer(
    ({
        onRemove,
        item,
        onChange,
        showingErrors,
    }: {
        onRemove: (item: ReplacementOptions) => void;
        item: ReplacementOptions;
        onChange: () => void;
        showingErrors: boolean;
    }) => {
        const theme = useMantineTheme();
        const change = useCallback((..._: any[]) => onChange(), [onChange]);
        const anyValueBadge = (
            <Badge size="md" variant="outline" sx={{ pointerEvents: 'none', background: theme.colors.gray[0] }} color="gray">
                Any Value
            </Badge>
        );
        const remove = useCallback(() => change(onRemove(item)), [onRemove, item, change]);
        const error = useCallback((value: string | null | undefined) => showingErrors && (value === undefined || value === ''), [showingErrors]);
        const handleValueOptionsCreated = useCallback((options: Array<{ value: string; label: ReactNode }>) => {
            options.unshift({ value: '\0', label: anyValueBadge });
            return options;
        }, []);
        const updateValue = useCallback(
            (value: string, prop: 'ValueToCheck' | 'ValueToReplace') => {
                item[prop] = value === '\0' ? null : value;
                onChange();
            },
            [item, onChange]
        );
        const handleCheckValueUpdated = useCallback((value: string) => updateValue(value, 'ValueToCheck'), [updateValue]);
        const handleReplaceValueUpdated = useCallback((value: string) => updateValue(value, 'ValueToReplace'), [updateValue]);
        const getValueIcon = (value?: string | null) => (value !== null ? {} : { iconWidth: 120, icon: anyValueBadge, placeholder: '' });

        return (
            <Card mb="md" p="xs" withBorder sx={{ width: 'min-content', background: theme.colors.gray[1] }} radius="md">
                <OptionLayout>
                    <Text>
                        <></>
                    </Text>
                    <Text size="sm" align="center">
                        Key
                    </Text>
                    <Text size="sm" align="center">
                        Value
                    </Text>
                </OptionLayout>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                        <OptionLayout>
                            <Text size="sm" component="span" align="right" mr="lg">
                                Find
                            </Text>
                            <FieldsBox bg={theme.colors.gray[3]}>
                                <TagKeyCombobox
                                    value={item.KeyToCheck ?? ''}
                                    onChange={(v) => change((item.KeyToCheck = v))}
                                    inputProps={{ error: error(item.KeyToCheck), placeholder: 'Select', label: '' }}
                                />
                            </FieldsBox>
                            <FieldsBox bg={theme.colors.gray[3]}>
                                <TagValueCombobox
                                    value={item.ValueToCheck ?? ''}
                                    onChange={handleCheckValueUpdated}
                                    tagKey={item.KeyToCheck ?? ''}
                                    inputProps={{
                                        error: error(item.ValueToCheck),
                                        placeholder: 'Select',
                                        label: '',
                                        ...getValueIcon(item.ValueToCheck),
                                    }}
                                    onOptionsCreated={handleValueOptionsCreated}
                                />
                            </FieldsBox>
                        </OptionLayout>
                        <Space h={2} />
                        <OptionLayout>
                            <Text size="sm" component="span" align="right" mr="lg">
                                Replace with
                            </Text>
                            <FieldsBox bg={theme.colors.primary[2]}>
                                <TagKeyCombobox
                                    value={item.KeyToReplace ?? ''}
                                    onChange={(v) => change((item.KeyToReplace = v))}
                                    inputProps={{ error: error(item.KeyToReplace), placeholder: 'Select', label: '' }}
                                />
                            </FieldsBox>
                            <FieldsBox bg={theme.colors.primary[2]}>
                                <TagValueCombobox
                                    value={item.ValueToReplace ?? ''}
                                    onChange={handleReplaceValueUpdated}
                                    tagKey={item.KeyToReplace ?? ''}
                                    inputProps={{
                                        error: error(item.ValueToReplace),
                                        placeholder: 'Select',
                                        label: '',
                                        ...getValueIcon(item.ValueToReplace),
                                    }}
                                    onOptionsCreated={handleValueOptionsCreated}
                                />
                            </FieldsBox>
                        </OptionLayout>
                    </Box>
                    <RemoveBox>
                        <Tooltip label="Remove Replacement" withinPortal position="right">
                            <ActionIcon onClick={remove}>
                                <Trash />
                            </ActionIcon>
                        </Tooltip>
                    </RemoveBox>
                </Box>
            </Card>
        );
    }
);

const RemoveBox = styled.div`
    margin-left: 4px;
    align-self: stretch;
    display: flex;
    align-items: center;
    background: ${(p) => p.theme.colors.gray[2]};
    border-radius: 5px;
    padding: 5px;
`;

const FieldsBox = styled.div<{ bg: string }>`
    padding: 5px 9px 5px 9px;
    &:first-of-type {
        border-radius: 5px 0 0 5px;
    }
    &:last-child {
        border-radius: 0 5px 5px 0;
    }
    &:first-of-type:last-child {
        border-radius: 5px;
    }

    background: ${(p) => p.bg};
`;

const OptionLayout = styled.div`
    display: grid;
    grid-template-columns: 120px 250px 250px;
    align-items: center;
`;
